import { Box, Group, Stack, Text, ThemeIcon, useMantineTheme } from '@mantine/core';
import { IconShieldCheck } from '@tabler/icons-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { latest } from '../types';
import type { RiskPoint } from '../types';
import { SafeTCardShell } from './SafeTCardShell';
import { safeTColors } from '../safeTColors';
import { StackedProgressBar } from './StackedProgressBar';
import { getMultiGoalStatus, getThresholdGoalState } from '../../statusUtils';
import { AppTooltip } from '../../../common/AppTooltip';

export function RiskPostureCard({ series }: { series: RiskPoint[] }) {
  const isEmpty = !series?.length;
  const cur = isEmpty ? null : latest(series);
  const prev = series.length > 1 ? series[series.length - 2] : null;

  const total = cur ? cur.appliedLow + cur.appliedMedium + cur.appliedHigh : 0;
  const lowPct = cur && total > 0 ? (cur.appliedLow / total) * 100 : 0;

  const lowThreshold = 80;
  const status = cur
    ? getMultiGoalStatus([
        getThresholdGoalState({ value: lowPct, goal: lowThreshold, direction: 'min' }),
      ])
    : { label: 'Snapshot' as const, color: 'gray' as const };

  const qoqPct = (curValue: number, prevValue?: number) =>
    prevValue && prevValue > 0 ? ((curValue - prevValue) / prevValue) * 100 : null;

  const formatChange = (value: number | null, suffix: 'QoQ' | 'YoY'): string => {
    if (value === null || !Number.isFinite(value)) return `— ${suffix}`;
    if (value === 0) return `0.00% ${suffix}`;
    const arrow = value > 0 ? '↑' : '↓';
    return `${arrow}${Math.abs(value).toFixed(2)}% ${suffix}`;
  };

  const currentQuarterTotal = total;
  const prevQuarterTotal = prev
    ? prev.appliedLow + prev.appliedMedium + prev.appliedHigh
    : null;
  const qoqQuarter = qoqPct(currentQuarterTotal, prevQuarterTotal ?? undefined);

  return (
    <SafeTCardShell
      title="Risk Posture"
      subtitle={cur?.quarter ?? 'No data'}
      statusLabel={status.label}
      statusColor={status.color}
      statusTooltip={status.tooltip}
      rightSlot={
        <ThemeIcon variant="light" size="lg">
          <IconShieldCheck size={18} />
        </ThemeIcon>
      }
    >
      <Stack spacing="sm">
        <Group position="apart" spacing="xs" noWrap>
          <Text size="xs" color="dimmed">
            Current Node Risk Mix
          </Text>
          <Text size="xs" color="dimmed">
            {currentQuarterTotal} Nodes · {formatChange(qoqQuarter, 'QoQ')}
          </Text>
        </Group>
        <StackedProgressBar
          segments={[
            {
              value: cur?.appliedLow ?? 0,
              color: safeTColors.success,
              label: 'Low Risk',
              qoqPct: qoqPct(cur?.appliedLow ?? 0, prev?.appliedLow),
            },
            {
              value: cur?.appliedMedium ?? 0,
              color: safeTColors.warning,
              label: 'Medium Risk',
              qoqPct: qoqPct(cur?.appliedMedium ?? 0, prev?.appliedMedium),
            },
            {
              value: cur?.appliedHigh ?? 0,
              color: safeTColors.danger,
              label: 'High Risk',
              qoqPct: qoqPct(cur?.appliedHigh ?? 0, prev?.appliedHigh),
            },
          ]}
          threshold={lowThreshold}
          thresholdLabel={`Healthy Threshold (Low ≥ ${lowThreshold}%)`}
        />
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px 12px',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: 6,
          }}
        >
          {[
            {
              label: 'Low',
              color: safeTColors.success,
              tooltip: 'Low risk has minimal impact and routine handling.',
            },
            {
              label: 'Medium',
              color: safeTColors.warning,
              tooltip: 'Medium risk has moderate impact and a planned fix timeline.',
            },
            {
              label: 'High',
              color: safeTColors.danger,
              tooltip: 'High risk has critical impact and immediate action.',
            },
          ].map((item) => (
            <AppTooltip key={item.label} label={item.tooltip} inline>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: item.color,
                    flex: '0 0 auto',
                  }}
                />
                <Text
                  size="xs"
                  color="dimmed"
                  component="span"
                  sx={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}
                >
                  {item.label}
                </Text>
              </Box>
            </AppTooltip>
          ))}
        </Box>
      </Stack>

      <Box sx={{ marginTop: '1rem' }}>
        <SimpleRiskLayout trendData={series} />
      </Box>
    </SafeTCardShell>
  );
}

function SimpleRiskLayout({
  trendData,
}: {
  trendData: Array<{ quarter: string; appliedLow: number; appliedMedium: number; appliedHigh: number }>;
}) {
  const theme = useMantineTheme();
  const dotStroke = theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)';
  const renderDot = (color: string) => (dotProps: any) => (
    <circle
      cx={dotProps.cx}
      cy={dotProps.cy}
      r={2}
      fill={color}
      stroke={dotStroke}
      strokeWidth={1.5}
    />
  );

  return (
    <Stack spacing={6}>
      <Text size="xs" color="dimmed">
        Risk Nodes Trend
      </Text>
      <Box sx={{ height: 190 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={trendData}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.25} vertical={false} />
          <XAxis
            dataKey="quarter"
            tick={{ fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={12}
          />
          <YAxis
            orientation="right"
            tick={{ fontSize: 11 }}
            allowDecimals={false}
            axisLine={false}
            tickLine={false}
            width={44}
            tickMargin={8}
          />
          <Line
            type="monotone"
            dataKey="appliedLow"
            name="Low"
            stroke={safeTColors.success}
            strokeWidth={2}
            dot={renderDot(safeTColors.success)}
            activeDot={{ r: 4 }}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="appliedMedium"
            name="Medium"
            stroke={safeTColors.warning}
            strokeWidth={2}
            dot={renderDot(safeTColors.warning)}
            activeDot={{ r: 4 }}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="appliedHigh"
            name="High"
            stroke={safeTColors.danger}
            strokeWidth={2}
            dot={renderDot(safeTColors.danger)}
            activeDot={{ r: 4 }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px 12px',
          alignItems: 'center',
          justifyContent: 'center',
          paddingTop: 12,
        }}
      >
        {[
          { label: 'Low', color: safeTColors.success },
          { label: 'Medium', color: safeTColors.warning },
          { label: 'High', color: safeTColors.danger },
        ].map((item) => (
          <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: item.color,
                flex: '0 0 auto',
              }}
            />
            <Text size="xs" color="dimmed" component="span">
              {item.label}
            </Text>
          </Box>
        ))}
      </Box>
    </Stack>
  );
}
